import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ContactPicker from '../../Pickers/ContactPicker';
import List from '@mui/material/List';
import { Contact } from '../../../common/models/Contacts';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import {
  GetBillingGroupAdditionalContacts,
  UpdateAdditionalContacts,
} from '../../../services/BillingGroupsService';

interface Props {
  open: boolean;
  billingGroupId: number;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}
export default function ManageAdditionalContactsDialog({
  open,
  billingGroupId,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    if (open) {
      const getData = async () => {
        const res = await GetBillingGroupAdditionalContacts(billingGroupId);
        if (res) {
          setContacts(res);
        }
      };

      getData();
    }
  }, [open]);

  const submitHandler = useCallback(async () => {
    loadingHandler && loadingHandler(true);

    const ids: number[] = contacts.map((contact) => contact.id);

    const data = await UpdateAdditionalContacts(billingGroupId, ids);
    if (data) {
      successHandler && successHandler('Additional Contacts Updated');
      closeHandler();
    } else {
      errorHandler && errorHandler('Something went wrong updating the Additional Contacts');
      closeHandler();
    }

    loadingHandler && loadingHandler(false);
  }, [loadingHandler, successHandler, errorHandler, closeHandler, billingGroupId, contacts]);

  const addContactHandler = useCallback(
    (c: Contact | null) => {
      if (c) {
        setContacts((prev) => {
          const existing = prev.find((v) => v.id === c.id);
          if (existing) {
            errorHandler && errorHandler('Contact Already Added');
            return prev;
          }
          const tmp = [...prev, c];
          return tmp;
        });
      }
    },
    [errorHandler]
  );

  const removeContactHandler = useCallback((index: number) => {
    setContacts((prev) => {
      const tmp = [...prev];
      tmp.splice(index, 1);
      return tmp;
    });
  }, []);

  const contactsListElement = useMemo(() => {
    return contacts.map((c, i) => {
      const hasEmail = Boolean(c.email);
      return (
        <React.Fragment key={i}>
          <ListItem
            disablePadding
            secondaryAction={
              <IconButton edge="end" onClick={() => removeContactHandler(i)}>
                <DeleteIcon />
              </IconButton>
            }
          >
            <ListItemButton dense>
              <ListItemText
                sx={{ color: (t) => (hasEmail ? undefined : t.palette.error.main) }}
                primary={c.fullName}
                secondary={hasEmail ? '' : 'Has No Email'}
              />
            </ListItemButton>
          </ListItem>
        </React.Fragment>
      );
    });
  }, [removeContactHandler, contacts]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>Manage Additional Contacts</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ContactPicker
              label="Add Contact"
              disableQuickCreate
              value={null}
              onSelectCallback={addContactHandler}
              blurOnSelect
            />
            <List>{contactsListElement}</List>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={submitHandler}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
